import React, { useState, useEffect, useCallback } from 'react';
import { useFirestore } from 'firebaseHooks/FirestoreContext';
import { useFunctions } from 'firebaseHooks/FunctionsContext';
import {
  doc, updateDoc, arrayUnion, arrayRemove,
} from 'firebase/firestore';
import {
  Paper, Typography, Button, Dialog, CircularProgress,
  DialogContent, DialogTitle,
  List, ListItem, ListItemText, TextField, IconButton, DialogActions,
  Link as MUILink, MenuItem,
} from '@mui/material';
import { useFormik } from 'formik';
import useGlobal from 'global-state/store';
import { useTranslation } from 'react-i18next';
import DeleteIcon from '@mui/icons-material/Delete';
import { httpsCallable } from 'firebase/functions';
import { LoadingButton } from '@mui/lab';

export default function OrgApp({ organizationId, app, members }) {
  const firestore = useFirestore();
  const [admins, setAdmins] = useState([]);
  const [supervisors, setSupervisors] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [supervisorDialogOpen, setSupervisorDialogOpen] = useState(false);
  const [supervisorCandidateId, setSupervisorCandidateId] = useState('');
  // Inventory only: operator rights plus read-only access to the Inventory admin screens.
  const supportsSupervisors = app.id === 'inventory';
  const [loadingState, setloadingState] = useState('initial');
  const [, globalActions] = useGlobal();
  const { t } = useTranslation();
  const functions = useFunctions();
  functions.region = 'europe-west1';
  const addMemberAsAppAdminCallable = httpsCallable(functions, 'addMemberAsAppAdmin');
  const [callLoading, setCallLoading] = useState(false);

  const toListEntry = useCallback((id) => {
    const member = members.find((m) => m.id === id);
    return { id, email: member?.email || 'Unknown' };
  }, [members]);

  useEffect(() => {
    async function loadAdmins() {
      setAdmins((app?.admins || []).map(toListEntry));
      setSupervisors((app?.supervisors || []).map(toListEntry));
      setloadingState('loaded');
    }

    if (loadingState === 'initial') {
      setloadingState('loading');
      loadAdmins();
    }
  }, [app?.admins, app?.supervisors, firestore, loadingState, organizationId, toListEntry]);

  const supervisorCandidates = members.filter((m) => m.role !== 'admin'
    && !admins.some((a) => a.id === m.id)
    && !supervisors.some((s) => s.id === m.id));

  const handleAddSupervisor = async () => {
    try {
      const appDocRef = doc(firestore, 'organizations', organizationId, 'apps', app.id);
      await updateDoc(appDocRef, { supervisors: arrayUnion(supervisorCandidateId) });
      setSupervisors([...supervisors, toListEntry(supervisorCandidateId)]);
      globalActions.setSnackbarMessage({ message: t('success'), severity: 'success' });
    } catch (error) {
      globalActions.setSnackbarMessage({ message: `Error adding supervisor: ${error.message}`, severity: 'error' });
    } finally {
      setSupervisorCandidateId('');
      setSupervisorDialogOpen(false);
    }
  };

  const handleRemoveSupervisor = async (supervisorId) => {
    const appDocRef = doc(firestore, 'organizations', organizationId, 'apps', app.id);
    await updateDoc(appDocRef, { supervisors: arrayRemove(supervisorId) });
    setSupervisors(supervisors.filter((s) => s.id !== supervisorId));
  };

  // Handler for removing an admin
  const handleRemoveAdmin = async (adminId) => {
    const appDocRef = doc(firestore, 'organizations', organizationId, 'apps', app.id);
    await updateDoc(appDocRef, { admins: arrayRemove(adminId) });
    const newAdmins = admins.filter((a) => a.id !== adminId);
    setAdmins(newAdmins);
  };

  const formik = useFormik({
    initialValues: {
      email: '',
    },
    validate: (values) => {
      const errors = {};
      if (!values.email) {
        errors.email = 'Email is required';
      } else if (!/\S+@\S+\.\S+/.test(values.email)) {
        errors.email = 'Invalid email address';
      } else if (!members.some((m) => m.email === values.email)) {
        errors.email = 'The admin must be one of the members of the organization';
      }
      return errors;
    },
    onSubmit: async (values) => {
      setCallLoading(true);
      try {
        const { email } = values;
        const addAdminResult = await addMemberAsAppAdminCallable({
          email,
          organizationId, // Assuming this is available in the scope
          appId: app.id, // Assuming this is available in the scope
        });

        // Assuming the Cloud Function returns the new admin's UID in newAdminId
        const newAdminUid = addAdminResult.data.newAdminId;

        // Find the member with the returned UID to update the local state
        const member = members.find((m) => m.id === newAdminUid);
        const newAdmins = [...admins, member];
        setAdmins(newAdmins);
        // Admin rights supersede the supervisor role; keep the two lists exclusive.
        if (supportsSupervisors && supervisors.some((s) => s.id === newAdminUid)) {
          await handleRemoveSupervisor(newAdminUid);
        }
        globalActions.setSnackbarMessage({ message: t('success'), severity: 'success' });
      } catch (error) {
        globalActions.setSnackbarMessage({ message: `Error adding admin: ${error.message}`, severity: 'error' });
      } finally {
        setCallLoading(false);
        setDialogOpen(false);
      }
    },
  });

  if (loadingState !== 'loaded') {
    return <CircularProgress />;
  }

  return (
    <Paper
      sx={{
        p: 3,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        gap: 1,
      }}
    >
      <Typography component="span" sx={{ fontWeight: 'bold' }}>{app.name}</Typography>
      <MUILink
        href={app.link}
        color="primary"
        sx={{ fontWeight: 'bold', fontSize: 12 }}
      >
        {t('to_app')}
      </MUILink>
      <Typography component="span" sx={{ fontSize: 14, alignSelf: 'flex-start', mt: 1 }}>Admins :</Typography>
      <List sx={{ p: 0 }}>
        {admins.map((admin) => (
          <ListItem
            key={admin.id}
            disablePadding
            dense
          >
            <ListItemText primary={admin.email} />
            <IconButton edge="end" aria-label="delete" onClick={() => handleRemoveAdmin(admin.id)} size="small">
              <DeleteIcon />
            </IconButton>
            {/* <ListItemIcon /> */}
          </ListItem>
        ))}
        <Button variant="outlined" color="primary" size="small" onClick={() => setDialogOpen(true)} sx={{ mt: 1 }}>
          {t('organizations.add_admin')}
        </Button>
      </List>
      {supportsSupervisors && (
        <>
          <Typography component="span" sx={{ fontSize: 14, alignSelf: 'flex-start', mt: 1 }}>
            {`${t('organizations.supervisors')} :`}
          </Typography>
          <Typography component="span" sx={{ fontSize: 12, alignSelf: 'flex-start', color: 'text.secondary' }}>
            {t('organizations.supervisors_help')}
          </Typography>
          <List sx={{ p: 0 }}>
            {supervisors.map((supervisor) => (
              <ListItem
                key={supervisor.id}
                disablePadding
                dense
              >
                <ListItemText primary={supervisor.email} />
                <IconButton
                  edge="end"
                  aria-label="delete"
                  onClick={() => handleRemoveSupervisor(supervisor.id)}
                  size="small"
                >
                  <DeleteIcon />
                </IconButton>
              </ListItem>
            ))}
            <Button
              variant="outlined"
              color="primary"
              size="small"
              onClick={() => setSupervisorDialogOpen(true)}
              disabled={supervisorCandidates.length === 0}
              sx={{ mt: 1 }}
            >
              {t('organizations.add_supervisor')}
            </Button>
          </List>
          <Dialog open={supervisorDialogOpen} onClose={() => setSupervisorDialogOpen(false)}>
            <DialogTitle>{t('organizations.add_supervisor')}</DialogTitle>
            <DialogContent>
              <TextField
                select
                margin="dense"
                label={t('member_role.member')}
                fullWidth
                value={supervisorCandidateId}
                onChange={(event) => setSupervisorCandidateId(event.target.value)}
              >
                {supervisorCandidates.map((member) => (
                  <MenuItem key={member.id} value={member.id}>{member.email}</MenuItem>
                ))}
              </TextField>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSupervisorDialogOpen(false)} color="primary">
                {t('cancel')}
              </Button>
              <Button onClick={handleAddSupervisor} color="primary" disabled={!supervisorCandidateId}>
                {t('add')}
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <form onSubmit={formik.handleSubmit}>
          <DialogTitle>{t('organizations.add_admin')}</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              id="email"
              name="email"
              label="Email"
              type="email"
              fullWidth
              value={formik.values.email}
              onChange={formik.handleChange}
              error={Boolean(formik.errors.email)}
              helperText={formik.errors.email}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)} color="primary">
              {t('cancel')}
            </Button>
            <LoadingButton
              loading={callLoading}
              type="submit"
              color="primary"
              disabled={formik.isSubmitting || !formik.isValid}
            >
              {t('add')}
            </LoadingButton>
          </DialogActions>
        </form>
      </Dialog>
    </Paper>
  );
}
